import { Currency, getCurrencySymbol } from "@redotech/money/currencies";
import {
  Alignment,
  CartLayoutType,
  EmailFormat,
  Size,
} from "@redotech/redo-model/email-builder";
import { Team } from "@redotech/redo-model/team";
import { TrackableLineItem } from "@redotech/redo-model/trackable";
import { UtmParameters } from "@redotech/redo-model/utm";
import { memo, useEffect, useState } from "react";

const PropertyKeysToDisplay = ["Custom Number", "Custom Number Cotton"];

export const EmailLineItem = memo(function EmailLineItem({
  alignment,
  item,
  imageSize,
  imageCornerRadius,
  textColor,
  format,
  showVariant,
  showQuantity,
  showPrice,
  showImage,
  showTitle,
  showProperties,
  children,
  additionalMetadata,
  stackType,
  isBuilder,
  isLastInRow = false,
  team,
  utm,
  showDummyImages,
}: {
  alignment: Alignment;
  item: Pick<
    TrackableLineItem,
    "title" | "variantTitle" | "image" | "properties" | "handle" | "priceSet"
  > & { quantity: number | string; price: number | string };
  imageSize: Size;
  imageCornerRadius: number;
  textColor: string;
  format: EmailFormat;
  showVariant: boolean;
  showQuantity: boolean;
  showPrice: boolean;
  showImage: boolean;
  showTitle: boolean;
  showProperties?: boolean;
  children?: React.ReactNode;
  additionalMetadata?: React.ReactNode;
  stackType: CartLayoutType;
  isBuilder: boolean;
  isLastInRow?: boolean;
  team: Team;
  utm: UtmParameters;
  /**
   * Most of the time, we want to show gray dummy boxes instead of real images in the builder.
   * The exception is when it's a STATIC products block, since no recommendation logic is needed to show that.
   */
  showDummyImages?: boolean;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const amp = format === EmailFormat.AMP;

  useEffect(() => {
    if (!amp) return;
    setRefreshKey(refreshKey + 1);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSize]);

  const rowLayout = stackType === CartLayoutType.ROWS;

  const imageContent =
    isBuilder || !item.handle ? (
      <LineItemImage
        amp={amp}
        fullWidth={!rowLayout}
        imageCornerRadius={imageCornerRadius}
        imageSize={imageSize}
        showDummyImage={showDummyImages}
        src={item.image.src}
      />
    ) : (
      <a href={utm.applyToUrl(`${team?.storeUrl}/products/${item.handle}`)}>
        <LineItemImage
          amp={amp}
          fullWidth={!rowLayout}
          imageCornerRadius={imageCornerRadius}
          imageSize={imageSize}
          showDummyImage={showDummyImages}
          src={item.image.src}
        />
      </a>
    );

  const detailsContent = (
    <div>
      {showTitle && (
        <div
          style={{
            fontWeight: "bold",
            paddingBottom: "8px",
            lineHeight: "1",
            fontSize: "16px",
          }}
        >
          {item.title}
        </div>
      )}
      {showVariant && (
        <div style={{ paddingBottom: "4px" }}>{item.variantTitle}</div>
      )}
      {showQuantity && (
        <div style={{ paddingBottom: "4px" }}>Quantity: {item.quantity}</div>
      )}
      {showPrice && (
        <div style={{ fontSize: "12px" }}>
          {item.priceSet ? (
            `Price: ${getCurrencySymbol(
              item.priceSet.presentment_money.currency_code as Currency,
            )}${item.priceSet.presentment_money.amount}`
          ) : (
            <span>
              Price:{" "}
              {typeof item.price === "number" ? `$${item.price}` : item.price}
            </span>
          )}
        </div>
      )}
      {showProperties && (
        <div>
          {Object.keys(item.properties).map((key) => {
            if (PropertyKeysToDisplay.includes(key) && item.properties[key]) {
              return (
                <div key={key}>
                  {key}: {item.properties[key]}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
      {additionalMetadata}
    </div>
  );

  return (
    <div
      key={refreshKey}
      style={{
        borderBottomWidth: rowLayout && !isLastInRow ? 1 : 0,
        borderBottomStyle: "solid",
        borderColor: "#0003",
        textAlign: "left",
      }}
    >
      <table style={{ width: "100%", borderSpacing: 0 }}>
        <tbody>
          {rowLayout ? (
            // Row-oriented layout: Image on the left, details on the right
            <tr>
              <td
                style={{
                  padding: `${verticalStackRowPadding}px 0`,
                  verticalAlign: "top",
                  width:
                    sizeRatio[imageSize] *
                    rootImageSize *
                    (amp ? ampImageScale : 1),
                }}
              >
                {showImage && imageContent}
              </td>
              <td
                style={{
                  padding: `${verticalStackRowPadding}px`,
                  verticalAlign: "top",
                  textAlign: "left",
                }}
              >
                <table style={{ width: "100%", borderSpacing: 0 }}>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          maxWidth: "155px",
                          verticalAlign: "top",
                          display: "inline-block",
                          boxSizing: "border-box",
                        }}
                      >
                        {detailsContent}
                      </td>
                      <td
                        style={{
                          float: "right",
                          whiteSpace: "nowrap",
                          verticalAlign: "top",
                          display: "inline-block",
                          boxSizing: "border-box",
                        }}
                      >
                        {children}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          ) : (
            // Column-oriented layout: Image on top, details below
            <>
              {/* Image Row */}
              <tr>
                <td
                  style={{
                    verticalAlign: "top",
                    textAlign: alignment,
                    paddingTop: "12px",
                  }}
                >
                  {showImage && imageContent}
                </td>
              </tr>
              {/* Details Row */}
              <tr>
                <td
                  style={{
                    verticalAlign: "top",
                    textAlign: alignment,
                    paddingTop: "16px",
                  }}
                >
                  <div style={{ textAlign: alignment }}>{detailsContent}</div>
                  <div style={{ marginTop: "10px" }}>{children}</div>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
});

export const verticalStackRowPadding = 24;
export const rootImageSize = 120;
export const sizeRatio: Record<Size, number> = {
  [Size.SMALL]: 0.8,
  [Size.MEDIUM]: 1,
  [Size.LARGE]: 1.2,
  [Size.CUSTOM]: 1, // TODO
};
export const ampImageScale = 0.775;

export const LineItemImage = memo(function LineItemImage({
  src,
  imageSize,
  imageCornerRadius,
  amp,
  fullWidth,
  showDummyImage,
}: {
  src?: string;
  imageSize: Size;
  imageCornerRadius: number;
  amp: boolean;
  fullWidth?: boolean;
  showDummyImage?: boolean;
}) {
  const height = rootImageSize * sizeRatio[imageSize];
  const styleProps = {
    borderRadius: imageCornerRadius,
    objectFit: "cover" as any,
    width: fullWidth ? "100%" : `${height}px`,
    height: fullWidth ? "auto" : `${height}px`,
  };
  if (!src || showDummyImage) {
    return (
      <div
        style={{
          ...styleProps,
          aspectRatio: 1,
          backgroundColor: "#a3a3a3",
          borderRadius: imageCornerRadius,
        }}
      />
    );
  }
  return amp ? (
    <amp-img
      height={height * ampImageScale}
      layout={fullWidth ? "responsive" : "fixed"}
      src={src}
      style={styleProps}
      width={height * ampImageScale}
    />
  ) : (
    <img
      src={src}
      style={styleProps}
      width={styleProps.width.replace("px", "")}
    />
  );
});
